import { request } from "@/lib/auth";

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  active: boolean;
  createdAt: string | null;
  workspaceId: string;
  workspaceName: string;
}

export interface ListUsersParams {
  search?: string;
  status?: "active" | "disabled";
}

export function listUsers(params: ListUsersParams = {}) {
  const query = new URLSearchParams();
  if (params.search) query.set("search", params.search);
  if (params.status) query.set("status", params.status);
  const qs = query.toString();
  return request<{ users: AdminUser[] }>(`/api/admin/users${qs ? `?${qs}` : ""}`);
}

export function updateUserActive(id: string, active: boolean) {
  return request<{ user: AdminUser }>(`/api/admin/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ active }),
  });
}

export interface AdminWorkspace {
  id: string;
  name: string;
  sector: "energy" | "agriculture";
  active: boolean;
  createdAt: string | null;
  userCount: number;
  assetCount: number;
}

export function listWorkspaces(search?: string) {
  const qs = search ? `?search=${encodeURIComponent(search)}` : "";
  return request<{ workspaces: AdminWorkspace[] }>(`/api/admin/workspaces${qs}`);
}

export interface WorkspaceDetail extends AdminWorkspace {
  isInternal: boolean;
}

export interface AssetCountByCountry {
  country: string;
  count: number;
}

export function getWorkspaceDetail(id: string) {
  return request<{ workspace: WorkspaceDetail; assetsByCountry: AssetCountByCountry[] }>(
    `/api/admin/workspaces/${id}`
  );
}

export function updateWorkspaceActive(id: string, active: boolean) {
  return request<{ workspace: WorkspaceDetail }>(`/api/admin/workspaces/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ active }),
  });
}

export function deleteWorkspace(id: string) {
  return request<void>(`/api/admin/workspaces/${id}`, { method: "DELETE" });
}

export interface AdminStats {
  counts: {
    totalCompanies: number;
    totalUsers: number;
    activeUsers: number;
    disabledUsers: number;
    disabledCompanies: number;
    totalAssets: number;
  };
  breakdowns: {
    companiesBySector: { sector: string; count: number }[];
    assetsByCountry: { country: string; count: number }[];
    assetsByAssetType: { assetTypeName: string | null; sector: string; count: number }[];
  };
  activity: {
    monthlyNewCompanies: { month: string; count: number }[];
    monthlyNewUsers: { month: string; count: number }[];
  };
}

export function getStats() {
  return request<AdminStats>("/api/admin/stats");
}
